import { useContext } from 'react';
import { encodeJson } from 'use-query-params';

import { deepSnakeCase, intoSnakeCase } from '@vimeo/gctl-api/fetchers/_helpers';
import {
  GetSubscriptionPlansResult,
  GetSubscriptionPlansFields,
} from '@vimeo/gctl-api/fetchers/subscription_plans';
import { useGetMeSubscriptionPlans } from '@vimeo/gctl-api/hooks/useMeSubscriptionPlans';
import { useGetSubscriptionPlans } from '@vimeo/gctl-api/hooks/useSubscriptionPlans';
import { SubscriptionPlans } from '@vimeo/gctl-api/models/components/schemas/subscription-plans/SubscriptionPlans';
import { useCampaignIdOverride } from '@vimeo/orion';
import { ViewerContext } from '@vimeo/viewer';

import { GetSubscriptionPlansFilters, SubPlanQuery } from './constants';

export const useGetSubscriptionPlansData = (
  plans?: Partial<SubscriptionPlans['tier']>[],
  promos?: Partial<Record<SubscriptionPlans['tier'], string>>,
  useMeSubscription = true,
  queryParams?: Partial<SubPlanQuery>,
): SubscriptionPlans[] | undefined => {
  const campaignIdOverride = useCampaignIdOverride();

  let _query: SubPlanQuery = {
    filter: plans
      ? (plans.map((plan) => intoSnakeCase(plan)) as GetSubscriptionPlansFilters)
      : undefined,
    promos: promos ? (encodeJson(promos) as string) : undefined,
  };

  if (queryParams) {
    _query = { ..._query, ...deepSnakeCase(queryParams) };
  }

  // Apply Orion campaign override if exists
  const effectiveCampaign = campaignIdOverride ?? _query.campaignId;
  if (effectiveCampaign) {
    _query.campaignId = effectiveCampaign;
  }

  return fetchViaGCTL(_query, useMeSubscription);
};

const fetchViaGCTL = (query: SubPlanQuery, useMeSubscription: boolean) => {
  const viewer = useContext(ViewerContext);
  const { data, error } = (
    viewer?.user && useMeSubscription ? useGetMeSubscriptionPlans : useGetSubscriptionPlans
  )(() => {
    if (!viewer) {
      return null;
    }

    if (viewer.vuid) {
      query['vuid'] = viewer.vuid;
    }

    return {
      select: [
        'currency',
        'discount',
        'id',
        'metadata',
        'price',
        'promotion',
        'name',
        'uri',
        'tier',
        'priceFormatted',
      ],
      query,
    };
  });

  /**
   * There's an issue with gctl's typing of this api. It's typed as an array of subcription plan objects,
   * but the actual data is the pagination object housing an array of subcription plan objects.
   * This is the workaround to avoid typing issues.
   */
  const response = data as
    | {
        [key: string]: any;
        data?: GetSubscriptionPlansResult<GetSubscriptionPlansFields>;
      }
    | undefined;

  // Temporarily supporting older data structure until everything is refactored.
  if (response && response.data) {
    return response.data;
  }

  if (error) {
    console.error('Unable to retrieve data from Subscription Plans Api', error);
  }
};
